import styles from "@/styles/category.module.scss"
import { useState, useEffect } from 'react'
import DefaultLayout from "@/components/DefaultLayout"
import SliderBarLeft from "@/components/SliderBarLeft"
import SliderBarRight from "@/components/SliderBarRight"
import ShopInfo from "@/components/ShopInfo"
import BreadCrum from '@/components/global/BreadCrum'
import GoodsComponentWithDiscountNew from '@/components/GoodsComponentWithDiscountNew'
import CategoryFilter from "@/components/category/CategoryFilter"
import FilterItem from "@/components/category/FilterItem"
import NewArrivals from "@/components/category/NewArrivals"
import LatestReviews from "@/components/category/LatestReviews"
import {
  CaretLeftOutlined,
  CaretRightOutlined,
  CloseOutlined
} from '@ant-design/icons';
import type { PaginationProps } from 'antd';
import { Select, Pagination } from 'antd';
import Head from "next/head"
import { getCookie, setCookie } from '@/utils/cookie'
import { useRouter } from 'next/router'
import GoogleADSense from "@/components/GoogleADSense"
import dayjs from 'dayjs'
import categoryApi from '@/service/modules/categoryApi'

import getConfig from 'next/config'
import Link from "next/link"
const { publicRuntimeConfig } = getConfig()
const apiDomain = publicRuntimeConfig.apiDomain

interface ProductItem {
  productID: string;
  name: string;
  url: string;
  imgUrl: string;
  reviewCount: number;
  price: number;
  normalPrice: number;
  freeShipping: number;
  onSale: number;
  reduced: number;
  isNew: number;
  position?: number,
  location?: string;
  isEmailOnly: any;
  inStock?: number;
  isGroup?: number;
  promotionTypeID: string | number;
  promotion: {
    productID: number;
    promotionType: number;
    promotionPrice: number;
    normalPrice: number;
    endDate: string;
    status: number;
    notified: number;
    pointRate: string;
    promotionRate: number;
  };
  review: {
    avgRank: number;
    total: number;
  };
  shortDescription?: string;
  visitor?: {
    weeklyVisitors: number;
  }
  rankNum?: number;
}

interface BreadcrumbItem {
  name: string;
  url: string;
  current: boolean;
}

// 定义分类项接口
interface sideFilterItem {
  name: string;
  description?: string;
  url: string;
  isSelected: boolean;
  count?: boolean;
}

interface filterTypeItem {
  title: string;
  key: string;
  items: sideFilterItem[]
}

// 分类筛选接口
interface cateFilterItem {
  name: string;
  categoryID: number | string;
  parent: number;
  url: string;
  count: number | string;
  isSelected?: boolean;
  isExpand?: boolean;
  subCategories: cateFilterItem[];
}

interface cateFilterType {
  title: string;
  key: string;
  items: cateFilterItem[]
}

interface Props {
  categoryID: number | string;
  categoryPath: string;
  breadCrumb: BreadcrumbItem[];
  sidebarFilter: filterTypeItem[];
  sidebarCateFilter: cateFilterType[];
  pageProductData: ProductItem[];
  page: number | undefined;
  perPage: number | undefined;
  lastPage: number;
  total: number | undefined;
  seo?: {
    title: string;
    keywords: string;
    description: string;
  },
  queryFilter: {
    filterName: string;
    brand: string;
    perPage: number | string;
    page: number | string;
    sortBy: string;
    sortDirection: string;
    maxPrice: number | string;
    minPrice: number | string;
    area: string;
  }
}

// 获取分类列表数据
async function getProductList(params: any) {
  try {
    const dataObj = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null && value !== '')
    );


    let productData = await categoryApi.getCategoryProduct(dataObj)

    if (
      productData.data 
      && productData.data.pageList 
      && productData.data.pageList.items
      && productData.data.pageList.items.length
    ) {
      productData.data.pageList.items.forEach((item: any) => {
        item.imgUrl = item.filename || item.newThumbnail
      })
    }

    return {
      ...productData.data
    }
  } catch (error) {
    console.log('Next.js Error :', dayjs().format('YY-MM-DD HH:mm:ss'), `  Page Category: ${params.categoryPath} Data acquisition failed:`, JSON.stringify(error))
  }
}

// 在服务器端渲染时调用接口，并返回 props 给页面组件
export async function getServerSideProps(context: any) {
  /**
   * 获取 URL 中的 参数
   * slug是一个数组
   * 不管slug有多少层级，在这里我们都只需要获取第一个参数（分类名）
   */
  const {
    slug,
    filterName,
    brand,
    perPage,
    page,
    sortBy,
    sortDirection,
    maxPrice,
    minPrice,
    area
  } = context.query

  /**
   * 获取url参数，提取出分类的路径部分 categoryPath
   */
  const path = slug[0]
  const regex = /([^?]+)/;

  // 使用正则表达式匹配并提取路径部分
  var match = path.match(regex)
  // 获取匹配到的结果
  let categoryPath = match ? match[1] : slug[0];

  const queryFilter = {
    filterName: filterName || null,
    brand: brand || null,
    perPage: perPage || 48,
    page: page || 1,
    sortBy: sortBy || 'popularity',
    sortDirection: sortDirection || 'desc',
    maxPrice: maxPrice || null,
    minPrice: minPrice || null,
    area: area || null
  }
  
  const data = await getProductList({categoryPath, ...queryFilter})

  if (data) {
    /**
     * 先把category的数据拿出来
     * 按分类的需求 增加数据需要的字段
     */
    let categoryOriginalData = data.sidebarFilter && data.sidebarFilter.length ? data.sidebarFilter.filter((item: any) => item.key == 'category') : []
    let categoryFilterData = JSON.parse(JSON.stringify(categoryOriginalData))
    if (categoryOriginalData.length) {
      categoryFilterData[0].items.forEach((cateFirstLevel: cateFilterItem) => {
        cateFirstLevel.isSelected = !!(cateFirstLevel.categoryID == data.categoryID)
        cateFirstLevel.url = `${apiDomain}${cateFirstLevel.url}`

        cateFirstLevel.subCategories.forEach((cateSecondLevel: cateFilterItem) => {
          cateSecondLevel.isSelected = !!(cateSecondLevel.categoryID == data.categoryID)
          cateSecondLevel.url = `${apiDomain}${cateSecondLevel.url}`

          /**
           * 如果有选中，则把三级分类打开
           */
          if (cateSecondLevel.isSelected) {
            cateSecondLevel.isExpand = true
          }

          /**
           * 看subCategories有没有选中的分类
           * 如果有，就把二级分类展开（isExpand设置为true）
           */
          let subHasSelected = false

          cateSecondLevel.subCategories.forEach((cateThirdLevel: cateFilterItem) => {
            cateThirdLevel.isSelected = !!(cateThirdLevel.categoryID == data.categoryID)
            cateThirdLevel.url = `${apiDomain}${cateThirdLevel.url}`
            if (cateThirdLevel.isSelected) subHasSelected = true
          })

          if (subHasSelected) cateSecondLevel.isExpand = true
        })
      })
    }

    let filtersData = data.sidebarFilter.filter((item: any) => item.key != 'category' && item.items.length)

    filtersData.forEach((item: any) => {
      if (item.key == 'filtered-by') {
        item.items.forEach((v:any) => {
          v.isSelected = queryFilter.filterName == v.name.replace(/\s/g, '')
          v.url = `${apiDomain}/${categoryPath}?filterName=${v.name.replace(/\s/g, '')}&sortBy=${v.sortBy}&sortDirection=${v.direction}`

          if (v.isSelected) {
            v.url = `${apiDomain}/${categoryPath}`
          }
        })
      }

      if (item.key == 'sort-by') {
        let hasSelected = false
        item.items.forEach((v:any) => {
          v.isSelected = queryFilter.filterName == (v.name + v.description).replace(/\s/g, '')
          v.url = `${apiDomain}/${categoryPath}?filterName=${(v.name + v.description).replace(/\s/g, '')}&sortBy=${v.sortBy}&sortDirection=${v.direction}`

          if (v.isSelected) {
            hasSelected = true
            v.url = `${apiDomain}/${categoryPath}`
          }
        })

        if (!hasSelected) {
          item.items.forEach((v:any) => {
            if (v.name == 'Bestselling') v.isSelected = true
          })
        } 
      }

      if (item.key == 'price-range') {
        let hasSelected = false
        item.items.forEach((v:any) => {
          v.isSelected = queryFilter.filterName == v.name.replace(/[\s$]/g, '');
          v.count = 0
          v.url = `${apiDomain}/${categoryPath}?filterName=${v.name.replace(/[\s$]/g, '')}&minPrice=${v.minPrice || 0}&maxPrice=${v.maxPrice}`

          if (v.isSelected) {
            hasSelected = true
            v.url = `${apiDomain}/${categoryPath}`
          }
        })

        // 拼接 All price选项
        let obj = {
          name: 'All Price',
          isSelected: hasSelected ? false : true,
          count: 0,
          url: `${apiDomain}/${categoryPath}`
        }
        item.items.push(obj)
      }


      if (item.key == 'hot-brand') {
        item.items = item.items.filter((v: any) => v.count)

        item.items.forEach((v:any) => {
          v.isSelected = queryFilter.filterName == v.name
          v.url = `${apiDomain}/${categoryPath}?filterName=${v.name}&brand=${v.name}`

          if (v.isSelected) {
            v.url = `${apiDomain}/${categoryPath}`
          }
        })
      }
    })

    return {
      props: {
        categoryID: data.categoryID,
        categoryPath,
        breadCrumb: data.breadCrumb || [],
        pageProductData: data.pageList.items || [],
        page: data.pageList.currentPage || queryFilter.page,
        perPage: data.pageList.perPage || queryFilter.perPage,
        lastPage: data.pageList.lastPage,
        total: data.pageList.total,
        sidebarFilter: filtersData || [],
        sidebarCateFilter: categoryFilterData || [],
        seo: data.seo,
        queryFilter
      }
    }
  } else {
    // 如果没有返回数据，返回一个 notFound 的标志，让 Next.js 渲染一个内置的 404 页面
    return {
      notFound: true,
    }
  }
}

export default function Category(props: Props) {
  const { Option } = Select

  // 生成跳转的Url
  const handleGenerateUrl = (params: any) => {
    // 拿到当前url上的参数, 以及需要调整的参数进行合并
    let filters = props.queryFilter || {}
    filters = {...filters, ...params}

    if (filters.brand != filters.filterName) {
      filters.brand = ''
    }

    if (filters.minPrice || filters.maxPrice) {
      filters.brand = ''
      filters.sortBy = ''
      filters.sortDirection = ''
    }

    // 剔除无用参数（值为null或者空字符串）
    let dataObj = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== null && value !== '')
    );
  
    const href = new URL(`/${props.categoryPath}`, `${apiDomain}`);
  
    // 遍历参数对象并添加至查询字符串
    Object.keys(dataObj).forEach(key => {
        href.searchParams.set(key, String(dataObj[key]));
    });

    return href
  };

  // 处理页面中的跳转url
  let freeShippingUrl = handleGenerateUrl({
    filterName: 'FreeShipping',
    sortBy: 'free-shipping',
    sortDirection: 'desc',
    brand: '',
    maxPrice: '',
    minPrice: ''
  })
  let freeShippingCancelUrl = handleGenerateUrl({
    filterName: '',
    sortBy: '',
    sortDirection: '',
    brand: '',
    maxPrice: '',
    minPrice: ''
  })
  let lastPageUrl = handleGenerateUrl({page: Number(props.queryFilter.page) - 1})
  let nextPageUrl = handleGenerateUrl({page: Number(props.queryFilter.page) + 1})

  let pageSizeList = [
    { 
      id: 1,
      name: '48',
      value: 48,
      url: handleGenerateUrl({perPage: 48})
    },
    { 
      id: 2,
      name: '100',
      value: 100,
      url: handleGenerateUrl({perPage: 100})
    },
    { 
      id: 3,
      name: '200',
      value: 200,
      url: handleGenerateUrl({perPage: 200})
    }
  ]

  interface hotSearchKeywordItem {
    keywords: string;
    url: string;
  }

  interface areaItem {
    name: string;
    url: string;
  }

  const [popularKeywords, setPopularKeywords] = useState('');
  const [hotSearchKeyword, setHotSearchKeyword] = useState<hotSearchKeywordItem[]>([]);
  const [shopArea, setShopArea] = useState<areaItem[]>([]);
  const [areaSuggestion, setAreaSuggestion] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [aboutCategory, setAboutCategory] = useState('');

  // 获取分类页面extra数据
  async function getCategoryExtraData() {
    try {
      let resData = await categoryApi.getCategoryExtra(props.categoryID)

      if (resData.data && resData.data) {
        setPopularKeywords(resData.data.popularKeywords)
        setHotSearchKeyword(resData.data.hotSearchKeyword)
        setAreaSuggestion(resData.data.areaSuggestion)
        setCategoryName(resData.data?.category.name || '')
        setAboutCategory(resData.data?.category.description || '')

        // area 数据处理
        if (resData.data.area && resData.data.area.length) {
          let areaList = resData.data.area.map((item: string) => {
            // 调整为首字母大写
            return {
              name: item.charAt(0).toUpperCase()+ item.slice(1),
              url: `/${props.categoryPath}?area=${item}`
            }
          })
          setShopArea(areaList)
        }
      }
    } catch (error) {
      console.log(`csotd数据获取失败： `, error)
    }
  }

  // 获取页面数据
  const getPageData = () => {
    // 获取分类页面extra数据
    getCategoryExtraData()
  }

  useEffect(() => {
    getPageData()
  }, [])

  const router = useRouter()

  // 判断是否有aid 或 recoveraid在url，有就将其
  useEffect(() => {
    const { aid, recoveraid }: any = router.query
    let lastAidValue = ''
    let lastRecoveraidValue = ''

    // 检查是否有"aid"参数，如有多个取最后
    if (Array.isArray(aid) && aid.length > 0) {
      lastAidValue = aid[aid.length - 1]
    } else if (typeof aid === 'string') {
      // 如果只有一个"aid"参数，它将是一个字符串
      lastAidValue = aid
    }

    // 检查是否有"recoveraid"参数，如有多个取最后
    if (Array.isArray(recoveraid) && recoveraid.length > 0) {
      lastRecoveraidValue = recoveraid[recoveraid.length - 1]
    } else if (typeof recoveraid === 'string') {
      // 如果只有一个"recoveraid"参数，它将是一个字符串
      lastRecoveraidValue = recoveraid
    }

    // 优先于异步请求，将aid存入cookie, 60秒过期（后续会发起请求重新设置）
    if (lastAidValue || lastRecoveraidValue) {
      let currentDomain = window.location.hostname;
      if(currentDomain == 'www.crazysales.com.au') {
        currentDomain = ".crazysales.com.au"
      }
      setCookie('affiliateid', lastAidValue || lastRecoveraidValue, { expires: 30, secure: true, domain: `${currentDomain}`, })
    }
  }, [router.query])

  // 发现本地有存储aid，重新请求数据
  const getProductDetailWithPromotion = async () => {
    if (!getCookie('affiliateid')) return 

    getPageData()
  }

  // cookie有留存aid参数，则更新Email价格
  useEffect(() => {
    setTimeout(() => {
      let affiliateid = getCookie('affiliateid')
      if (affiliateid) getProductDetailWithPromotion()
    }, 400)
  }, [])

  // 分页器
  const onChange: PaginationProps['onChange'] = (current) => {
    window.location.href = String(handleGenerateUrl({page: current}))
  };

  interface suburbOptionsItem {
    id: string;
    name: string
  }
  const [suburbOptions, setSuburbOptions] = useState<suburbOptionsItem[]>([]);
  const [sortBy, setSortBy] = useState<string | null | undefined>(null);
  const [pageNum, setPageNum] = useState(0)
  const [pageSize, setPageSize] = useState(0)
  const [totalPage, setTotalPage] = useState(0)

  // 设置页面选中的参数
  useEffect(() => {
    setPageNum(Number(props.queryFilter.page))
    setPageSize(Number(props.queryFilter.perPage))
    setTotalPage(Number(props.lastPage))

    // 匹配 Filter by 参数
    switch (props.queryFilter.filterName) {
      case 'BestMatch':
        setSortBy('1')
        break
      case 'PriceHightoLow':
        setSortBy('3')
        break
      case 'PriceLowtoHigh':
        setSortBy('4')
        break
      case 'NewArrivals':
        setSortBy('5')
        break
      default:
        setSortBy('2')
    }

    setSuburbOptions(
      [
        { id: '1', name: 'Best Match' },
        { id: '2', name: 'Best Selling' },
        { id: '3', name: 'Price(High to Low)' },
        { id: '4', name: 'Price(Low to High)' },
        { id: '5', name: 'New Arrivals' }
      ]
    )
  }, [])

  // 获取一份初始化的参数
  const getInitFilterparams = () => {
    return {
      filterName: '',
      brand: '',
      perPage: '48',
      page: '1',
      sortBy: '',
      sortDirection: '',
      maxPrice: '',
      minPrice: '',
      area: ''
    }
  }

  // Sort by选择器触发事件
  const handleChangeSort = (value: string) => {
    let url = new URL(`${apiDomain}/${props.categoryPath}`);

    // 匹配 Filter by 参数
    switch (String(value)) {
      case '1':
        url = handleGenerateUrl(
          {
            ...getInitFilterparams(),
            ...{
              filterName: 'BestMatch',
              perPage: 48,
              page: 1,
              sortBy: 'bestMatch',
              sortDirection: 'desc'
            }
          })
        break
      case '2':
        url = handleGenerateUrl(
          {
            ...getInitFilterparams(),
            ...{
              filterName: 'Bestselling',
              perPage: 48,
              page: 1,
              sortBy: 'popularity',
              sortDirection: 'desc'
            }
          })
        break
      case '3':
        url = handleGenerateUrl(
          {
            ...getInitFilterparams(),
            ...{
              filterName: 'PriceHightoLow',
              perPage: 48,
              page: 1,
              sortBy: 'price',
              sortDirection: 'desc'
            }
          })
        break
      case '4':
        url = handleGenerateUrl(
          {
            ...getInitFilterparams(),
            ...{
              filterName: 'PriceLowtoHigh',
              perPage: 48,
              page: 1,
              sortBy: 'price',
              sortDirection: 'asc'
            }
          })
        break
      case '5':
        url = handleGenerateUrl(
          {
            ...getInitFilterparams(),
            ...{
              filterName: 'NewArrivals',
              perPage: 48,
              page: 1,
              sortBy: 'addTime',
              sortDirection: 'desc'
            }
          })
        break
      default:
    }

    window.location.href = String(url)
    setSortBy(value)
  };

  /**
   * emarsys tracking数据推送
   */
  useEffect(() => {
    // ScarabQueue.push(["category", String(${props.categoryPath})]);


    const subdomain = document.location.protocol === 'https:' ? 'https://recommender' : 'http://cdn';
    const id = 'scarab-js-api';

    if (document.getElementById(id)) return;

    const js = document.createElement('script');
    js.id = id;
    js.src = `${subdomain}.scarabresearch.com/js/1CBC47CFE1C81625/scarab-v2.js`;

    js.onload = () => {

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.innerHTML = `
            var ScarabQueue = ScarabQueue || [];
            if (localStorage.getItem('userEmail')) {
              ScarabQueue.push(['setEmail', localStorage.getItem('userEmail')]);
            }

            var categoryPath = Array.isArray(props.breadCrumb)
              ? props.breadCrumb
                  .map(item => item && item.name ? item.name : '')
                  .filter(name => name) // 过滤掉可能的空字符串
                  .join(' > ')
              : '';
              ScarabQueue.push(['category', '${
                Array.isArray(props.breadCrumb)
                  ? props.breadCrumb
                      .map(item => (item && item.name ? item.name : ''))
                      .filter(name => name)
                      .join(' > ')
                  : ''
              }']);
            ScarabQueue.push(['go']);
        `;

        document.body.appendChild(script);
    };

    const fs = document.getElementsByTagName('script')[0];
    fs?.parentNode?.insertBefore(js, fs);

    return () => {
        js.remove();
    };
  }, []);

  return (
    <>
      <Head>
        <title>{props?.seo?.title}</title>
        <meta name="keywords" content={props?.seo?.keywords || `Online Shopping Australia, online shopping sites, Home Shopping, Shopping Deals, Best Deals, Home Garden, Pet Supplies, Sports Fitness, Bedding Bath, Buy Online, Discount Shopping, Toys Hobbies, Beauty Health, Crazy Sales, Computers, IT, Christmas, Electronics, Automotive, Tools, Appliances, Baby, CrazySales`}></meta>
        <meta name="description" content={props?.seo?.description || `Australia's #1 online shopping website! Cheap bargains & discounts with the top online shopping Australia store! From furniture, to toys & hobbies, to appliances & pet supplies -- we've got it all, with free shipping on selected items!`}></meta>
      </Head>

      <DefaultLayout notStickyNavbar={true}>
        <div className={`${styles['page-category']} slider-wapper`}>
          <SliderBarLeft></SliderBarLeft>
          <SliderBarRight></SliderBarRight>

          {/* 面包屑 */}
          {
            !!(props?.breadCrumb && props.breadCrumb.length) &&
            <BreadCrum BreadList={props.breadCrumb}></BreadCrum>
          }

          {/* 商城特色介绍 */}
          <div className={styles['shop-info-box']}>
            <ShopInfo hiddenContactus={true}></ShopInfo>
          </div>

          <div className={styles['page-container']}>
            <div className={styles['filter-box']}>

              {/* 筛选 */}
              {
                !!(props.sidebarFilter && props.sidebarFilter.length) &&
                <div className={styles['filter-list']}>
                  {
                    props.sidebarFilter.map((item: filterTypeItem) => (
                      <div className={styles['filter-item']} key={item.key}>
                        <FilterItem filterTypeItem={item}></FilterItem>
                      </div>
                    ))
                  }
                </div>
              }

              {/* 分类的筛选 */}
              <div className={styles['category-filter']}>
                <div className={styles['category-filter-content']}>
                  <CategoryFilter cateFilterData={props.sidebarCateFilter}></CategoryFilter>
                </div>
              </div>

              {/* 新品上市或者热门商品推荐 */}
              {
                !!(props.categoryID) && 
                <div className={styles['new-arrivals']}>
                  <NewArrivals 
                    category={props.categoryID}
                    title={'New Arrivals & Hot'}
                  ></NewArrivals>
                </div>
              }

              {/* 广告 */}
              <div style={{ marginTop: '20px' }}>
                <GoogleADSense />
              </div>
            </div>

            <div className={styles['container-box']}>
              <div className={styles['top-filter-box']}>
                <div className={styles['top-jump']}>
                  <div>
                    <span className={`${styles['high-light']} margin-right-5`}>{props.total}</span>
                    Results for
                    <span className={`${styles['high-light']} margin-left-5`}>{ props.breadCrumb[`${props.breadCrumb.length - 1}`]?.name }</span>
                  </div>

                  <div className={styles['top-jump-box']}>
                    <div><span className={`${styles['high-light']}`}>{props.page}</span> / {props.lastPage}</div>
                    {/* 上一页 */}
                    {
                      pageNum <= 1 ?
                      <div 
                        className={`${styles['top-jump-item']} ${pageNum <= 1 ? styles['disabled'] : ''}`}
                      ><CaretLeftOutlined style={ pageNum <= 1 ? { color: '#dddddd' } : undefined }/></div>
                      :
                      <Link
                        href={lastPageUrl}
                        legacyBehavior
                      >
                        <a rel="noopener noreferrer">
                          <div 
                            className={`${styles['top-jump-item']} ${pageNum <= 1 ? styles['disabled'] : ''}`}
                          ><CaretLeftOutlined style={ pageNum <= 1 ? { color: '#dddddd' } : undefined }/></div>
                        </a>
                      </Link>
                    }

                    {/* 下一页 */}
                    {
                      pageNum >= totalPage ?
                        <div 
                      className={`${styles['top-jump-item']} ${pageNum >= totalPage ? styles['disabled'] : ''}`}
                      ><CaretRightOutlined /></div>
                      :
                      <Link
                        href={nextPageUrl}
                        legacyBehavior
                      >
                        <a rel="noopener noreferrer">
                          <div 
                            className={`${styles['top-jump-item']} ${pageNum >= totalPage ? styles['disabled'] : ''}`}
                          ><CaretRightOutlined /></div>
                        </a>
                      </Link>
                    }
                  </div>
                </div>

                <div className={styles['page-size-filter']}>
                  <div className={styles['page-size']}>
                    Show:&nbsp;&nbsp;
                    {
                      pageSizeList.map((item: any) => (
                        <Link
                          href={item.url}
                          legacyBehavior
                          key={item.id}
                        >
                          <a rel="noopener noreferrer">
                            <div 
                              className={`
                                ${styles['page-size-item']}
                                ${pageSize == item.value ? styles['active'] : ''}
                              `}
                            >{item.name}</div>
                          </a>
                        </Link>
                      ))
                    }
                  </div>

                  <div className={styles['select-filter']}>
                    <div className={styles['select-filter-item']}>
                      Filtered by:&nbsp;

                      {
                        props.queryFilter.filterName == 'FreeShipping' ?
                        <Link
                          href={freeShippingCancelUrl}
                          legacyBehavior
                        >
                          <a rel="noopener noreferrer">
                          <div className={styles['cancel-free-shipping']}>
                            Free Shipping
                            <div className={styles['cancel-btn']}>
                              <CloseOutlined style={{color: '#FFFFFF'}} />
                            </div>
                          </div>
                          </a>
                        </Link>
                        :
                        <Link
                          href={freeShippingUrl}
                          legacyBehavior
                        >
                          <a rel="noopener noreferrer">
                          <div className={styles['select-free-shipping']}>
                            Free Shipping
                          </div>
                          </a>
                        </Link>
                      }
                    </div>

                    <div className={styles['select-filter-item']}>
                      Sort by:&nbsp;

                      {
                        !!(sortBy) &&
                        <Select 
                          onChange={handleChangeSort} 
                          size={'small'}
                          style={{ width: '180px' }}
                          defaultValue={sortBy}
                        >
                          {
                            !!(suburbOptions && suburbOptions.length) &&

                            suburbOptions.map((item: suburbOptionsItem) => (
                              <Option 
                                value={item.id}
                                key={item.id}
                              >
                                {item.name}
                              </Option>
                            ))
                          }
                        </Select>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles['product-list']}>
                {
                  props.pageProductData && props.pageProductData.length
                  ?
                  props.pageProductData.map((product: ProductItem, index: number) => (
                    <div className={styles['product-item']} key={index}>
                      <GoodsComponentWithDiscountNew {...{ ...product, productName: product.name, imgUrl: product.imgUrl }} />
                    </div>
                  ))
                  :
                  <div className={styles['product-empty']}>Sorry, no products available with the selected options.</div>
                }
              </div>

              {/* 分页器 */}
              {
                !!(props.total && ((Number(props.total) / Number(props.perPage)) >= 1)) &&
                <div className={styles['pagination-box']}>
                  <Pagination
                    showQuickJumper
                    showSizeChanger={false}
                    onChange={onChange}
                    defaultCurrent={props.page}
                    defaultPageSize={props.perPage}
                    total={props.total}
                  />
                </div>
              }

              {
                !!(props.categoryID) && 
                <LatestReviews categoryId={props.categoryID}></LatestReviews>
              }
              
              {
                !!(hotSearchKeyword && hotSearchKeyword.length) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['popular-category']}>
                    Hot Searches:&nbsp;
                    {
                      hotSearchKeyword.map((item: hotSearchKeywordItem, index: number) => (
                        <span key={item.url}>
                          <Link
                            href={item.url}
                            legacyBehavior
                          >
                            <a rel="noopener noreferrer">
                              {item.keywords}
                            </a>
                          </Link>
                          {
                            !!(index + 1 < hotSearchKeyword.length) &&
                            <span>&nbsp;,&nbsp;</span>
                          }
                        </span>
                      ))
                    }
                  </div>
                </div>
              }

              {
                !!(popularKeywords) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['popular-category']} dangerouslySetInnerHTML={{ __html: `Popular Category: ${popularKeywords}` }}></div>
                </div>
              }
              
              {
                !!(shopArea && shopArea.length) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['popular-category']}>
                    Shop By City:&nbsp;
                    {
                      shopArea.map((item: areaItem, index: number) => (
                        <span key={index}>
                          <Link
                            href={item.url}
                            legacyBehavior
                          >
                            <a rel="noopener noreferrer">
                              {item.name}
                            </a>
                          </Link>
                          {
                            !!(index + 1 < shopArea.length) &&
                            <span>&nbsp;,&nbsp;</span>
                          }
                        </span>
                      ))
                    }
                  </div>
                </div>
              }
              
              {
                !!(areaSuggestion) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['popular-category']} dangerouslySetInnerHTML={{ __html: `${areaSuggestion}` }}></div>
                </div>
              }
              
              {
                !!(aboutCategory) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['header']}>
                    About&nbsp;{categoryName}
                  </div>

                  <div className={styles['content']} dangerouslySetInnerHTML={{ __html: aboutCategory }}></div>
                </div>
              }
              
              <div style={{ marginTop: '20px' }}>
                <GoogleADSense />
              </div>
            </div>
          </div>

        </div>
      </DefaultLayout>
    </>
  )
}
